import { useMount } from 'ahooks';
import { PlatformConfig } from 'components/StoreScopes';
import { useConfigList } from 'components/StoreScopes/useConfigList';
import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';

type RegionScope = General.StoreScope['regionStoreScopes'][0];

export function applyScope2storeScopes(
  applyScope: Remind.ApplyScope,
  storeCountConfig: PlatformConfig[],
): General.StoreScope[] {
  const {
    scope,
    storesScope: { tunnelTypeRegion = {} } = {},
    tunnelTypeScope: { tunnelTypes = [] } = {},
    tunnelTypeRegionScope: { tunnelTypeRegion: tunnelTypeRegions = {} } = {},
  } = applyScope;

  if (scope === 'ALL') {
    return [];
  }

  return Object.keys(tunnelTypeRegion).reduce(
    (res, platform) => {
      const { [platform]: { regionStore = {} } = {} } = tunnelTypeRegion;

      const regionStoreScopes = Object.keys(regionStore).reduce<RegionScope[]>((res, region) => {
        const { [region]: { storeIds = [] } = {} } = regionStore;

        if (storeIds.length > 0) {
          res.push({
            region,
            storeIds: storeIds.map(Number),
            storeCount: storeIds.length,
          });
        }

        return res;
      }, []);

      const storeCount = regionStoreScopes.reduce((res, { storeCount }) => res + storeCount, 0);

      const platformScope = res.find((_) => _.platform === platform);

      if (!platformScope) {
        res.push({
          platform,
          regionStoreScopes,
          storeCount,
        });

        return res;
      }

      platformScope.regionStoreScopes.push(...regionStoreScopes);
      platformScope.storeCount += storeCount;

      return res;
    },
    Object.keys(tunnelTypeRegions).reduce(
      (res, platform) => {
        const { [platform]: { regions = [] } = {} } = tunnelTypeRegions;

        const regionStoreScopes = regions.map<RegionScope>((region) => {
          return {
            region,
            storeIds: [],
            storeCount:
              storeCountConfig
                .find((_) => _.platform === platform)
                ?.regionList.find((_) => _.region === region)?.count || 0,
          };
        });

        const storeCount = regionStoreScopes.reduce((res, { storeCount }) => res + storeCount, 0);

        const platformScope = res.find((_) => _.platform === platform);

        if (!platformScope) {
          res.push({
            platform,
            regionStoreScopes,
            storeCount,
          });

          return res;
        }

        platformScope.regionStoreScopes.push(...regionStoreScopes);
        platformScope.storeCount += storeCount;

        return res;
      },
      tunnelTypes.map<General.StoreScope>((platform) => {
        const storeCount = storeCountConfig.find((_) => _.platform === platform)?.count || 0;

        return {
          platform,
          regionStoreScopes: [
            {
              region: 'all',
              storeIds: [],
              storeCount,
            },
          ],
          storeCount,
        };
      }, []),
    ),
  );
}

export function useApplyScope2storeScopes(...props: Parameters<typeof useStoreCountConfig>) {
  const storeCountConfig = useStoreCountConfig(...props);

  return {
    storeCountConfig,
    applyScope2storeScopes: useCallback(
      (applyScope: Remind.ApplyScope) => applyScope2storeScopes(applyScope, storeCountConfig),
      [storeCountConfig],
    ),
  };
}

export function storeScopes2applyScope(storeScopes: General.StoreScope[]): Remind.ApplyScope {
  return storeScopes.reduce<Remind.ApplyScope>(
    (res, platformScope) => {
      const { platform, regionStoreScopes } = platformScope;

      if (
        regionStoreScopes.length === 1 &&
        regionStoreScopes[0].region === 'all' &&
        regionStoreScopes[0].storeIds?.length === 0
      ) {
        res.tunnelTypeScope.tunnelTypes.push(platform);

        return res;
      }

      return regionStoreScopes.reduce((res, regionScope) => {
        const { region, storeIds = [] } = regionScope;

        if (region === 'all') {
          return res;
        }

        if (storeIds.length === 0) {
          const platformConfig = res.tunnelTypeRegionScope.tunnelTypeRegion[platform];

          if (platformConfig) {
            platformConfig.regions.push(region);
          } else {
            res.tunnelTypeRegionScope.tunnelTypeRegion[platform] = {
              regions: [region],
            };
          }

          return res;
        }

        const platformConfig = res.storesScope.tunnelTypeRegion[platform];

        if (!platformConfig) {
          res.storesScope.tunnelTypeRegion[platform] = {
            regionStore: {
              [region]: {
                storeIds: storeIds.map(String),
              },
            },
          };

          return res;
        }

        const regionConfig = platformConfig.regionStore[region];

        if (regionConfig) {
          regionConfig.storeIds.push(...storeIds.map(String));
        } else {
          platformConfig.regionStore[region] = {
            storeIds: storeIds.map(String),
          };
        }

        return res;
      }, res);
    },
    {
      scope: 'STORES',
      storesScope: {
        tunnelTypeRegion: {},
      },
      tunnelTypeScope: {
        tunnelTypes: [],
      },
      tunnelTypeRegionScope: {
        tunnelTypeRegion: {},
      },
    },
  );
}

export function useGetTaskName() {
  const { t } = useTranslate();

  const getTaskName = useCallback(
    ({ isDefault, taskName }: Pick<Remind.BasicConfig, 'isDefault' | 'taskName'>) => {
      if (!isDefault) {
        return taskName;
      }

      return taskName === '重要提醒' ? t('remind.重要提醒') : taskName;
    },
    [t],
  );

  return {
    t,
    getTaskName,
  };
}

export interface FormValue
  extends Util.PartialPartial<Omit<Remind.BasicConfig, 'isDefault'>, 'taskId'> {}

export type FV = FormValue;

export function useStoreCountConfig(props: Partial<Parameters<typeof useConfigList>[0]>) {
  const { configList, fetch } = useConfigList({
    supportWa: false,
    ...props,
  });

  useMount(() => {
    fetch().catch(() => {});
  });

  return configList;
}
